import { CopyField } from './CopyField'

/**
 * Meldet, was ein Agent aus einer Konfiguration gemacht hat.
 *
 * Fund aus dem Echtbetrieb: ein Agent, der älter war als die Zentrale, traf
 * auf eine Metrik-Art, die er nicht kannte, lehnte die ganze Konfiguration ab
 * und blieb still auf dem vorigen Stand. Gesagt hat er es nur in seinem
 * eigenen Log — die Oberfläche zeigte „noch nicht abgefragt" ohne Grund.
 */
export interface KonfigurationsLage {
  /** Der ganze Stand wurde abgelehnt; der Agent läuft auf dem vorigen. */
  fehler?: string
  /** Genau dieses Gerät wird deshalb gar nicht abgefragt. */
  uebersprungen?: boolean
  /** Einzelne Metriken oder Tabellen, die der Agent ausgelassen hat. */
  ausgelassen?: string[]
  agentName?: string
  agentVersion?: string
  /** Befehl, der den Agent auf seinem Host erneuert. */
  befehl?: string
}

/** Überschrift und Erklärung passend zur Schwere. */
function text(lage: KonfigurationsLage): { titel: string; erklaerung: string } | null {
  if (lage.fehler) {
    return {
      titel: 'Konfiguration abgelehnt: Agent zu alt – bitte aktualisieren',
      erklaerung:
        'Der Agent hat den neuen Stand nicht übernommen und arbeitet weiter mit dem vorigen. ' +
        'Das passiert, wenn die Zentrale etwas erhebt, das diese Fassung des Agents noch nicht kennt.',
    }
  }
  if (lage.uebersprungen) {
    return {
      titel: 'Dieses Gerät wird nicht abgefragt: Agent zu alt – bitte aktualisieren',
      erklaerung:
        'Von seinem Template ist beim Agent nichts übrig geblieben, das er abfragen kann. ' +
        'Deshalb steht hier kein Messwert.',
    }
  }
  if (lage.ausgelassen && lage.ausgelassen.length > 0) {
    return {
      titel: 'Der Agent lässt einen Teil aus – bitte aktualisieren',
      erklaerung:
        'Der Agent fragt ab, was er versteht, und überspringt den Rest. ' +
        'Die fehlenden Werte kommen, sobald er auf dem Stand der Zentrale ist.',
    }
  }
  return null
}

/**
 * Der Hinweisstreifen. Ohne Befund erscheint nichts — die Lage ist der
 * Normalfall, nicht die Ausnahme.
 */
export function KonfigurationsHinweis({ lage }: { lage: KonfigurationsLage }) {
  const inhalt = text(lage)
  if (!inhalt) return null

  const schwer = Boolean(lage.fehler || lage.uebersprungen)
  return (
    <section
      className="card"
      role="alert"
      style={{
        borderColor: schwer ? 'var(--crit)' : 'var(--warn)',
        background: schwer ? 'var(--crit-bg)' : 'var(--warn-bg)',
      }}
    >
      <div className="card-head">
        <h2 className="card-title">{inhalt.titel}</h2>
        {lage.agentVersion ? (
          <span className="muted" style={{ fontSize: 12 }}>
            {lage.agentName ? `${lage.agentName} · ` : ''}Fassung{' '}
            <span className="mono">{lage.agentVersion}</span>
          </span>
        ) : null}
      </div>
      <div className="card-body stack" style={{ gap: 12 }}>
        <p style={{ margin: 0 }}>{inhalt.erklaerung}</p>
        {lage.fehler ? (
          <p className="mono" style={{ margin: 0, fontSize: 12 }}>
            {lage.fehler}
          </p>
        ) : null}
        {lage.ausgelassen && lage.ausgelassen.length > 0 ? (
          <div>
            <div className="muted" style={{ fontSize: 12 }}>
              Ausgelassen:
            </div>
            <ul className="mono" style={{ margin: '4px 0 0', paddingLeft: 20, fontSize: 12 }}>
              {lage.ausgelassen.map((eintrag) => (
                <li key={eintrag}>{eintrag}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {lage.befehl ? (
          <CopyField label="Agent aktualisieren (auf dem Rechner im Kundennetz)" value={lage.befehl} />
        ) : null}
      </div>
    </section>
  )
}
